/**
 * What playing pays — the earn side of the economy.
 *
 * PURE. Every function here takes plain numbers and returns plain numbers, so
 * the whole reward table is testable without a match, a wallet, or a browser.
 * Storage lives in wallet.ts; this file only decides amounts.
 *
 * ── The rates, and what they mean ───────────────────────────────────────────
 *
 * Set by the owner: a win is 20, playing at all is 5, a goal is 1. Additive, so
 * a 2-1 win pays 27 and a defeat pays 5.
 *
 * Read against the catalogue (5,000 and 10,000) that is a LONG-HORIZON economy:
 * roughly 200 matches for the cheaper animation and 400 for the dearer. Coins
 * are a slow accumulation and real money is the fast route. That is a real and
 * common model, but it only stays honest if nobody quietly re-tunes one side
 * without looking at the other, which is why the arithmetic is written down in
 * both files.
 *
 * ── Losing still pays ───────────────────────────────────────────────────────
 *
 * A defeat earns the 5, never nothing. An economy that pays zero for a loss
 * punishes the exact moment a player is already unhappy, and "I got nothing for
 * that" ends more sessions than "I earned too little".
 *
 * ── Nothing here can be bought ──────────────────────────────────────────────
 *
 * Coins are earned by playing or bought as a convenience. They buy cosmetics
 * only (types.ts explains why the schema itself refuses gameplay effects), so
 * no amount of spending changes a single match.
 */

import type { Coins } from './types.js';

/*
 * OWNER'S RATES (2026-08-09): "winning 1 match is 20 coins and playing 1 match
 * is 5 coins, 1 coin for 1 goal".
 *
 * Additive: every finished match pays PLAYED, a win adds WIN on top, and each
 * goal adds one. So a 2-1 win is 5 + 20 + 2 = 27 and a 0-1 defeat is 5.
 *
 * These are deliberately much tighter than the earlier 100/45/25 table, and the
 * catalogue is priced at 5,000-10,000, so an item is several hundred matches of
 * play. That is a long-horizon economy where coins are a slow accumulation and
 * real money is the fast route — a legitimate model, and one that only works if
 * the prices stay honest about it. The arithmetic is spelled out here so nobody
 * re-tunes one side without seeing the other.
 */
export const WIN_COINS = 20;
/** Every finished match pays this, win or lose. */
export const PLAYED_COINS = 5;
export const GOAL_COINS = 1;

/*
 * Competition payouts, kept in proportion to the match rates above.
 *
 * A knockout round is worth a handful of matches and a trophy a run of them —
 * they are the reason to enter a tournament rather than replay friendlies, so
 * they must be clearly better per match without becoming a shortcut past the
 * catalogue's prices.
 */
export const WC_ROUND_COINS: readonly Coins[] = [30, 60, 120, 250];
export const WC_TROPHY_COINS = 500;
export const WC_QUALIFY_COINS = 50;

export const SEASON_COMPLETE_COINS = 80;
export const LEAGUE_TITLE_COINS = 400;
/** Top four but no title — effort that fell short still counts for something. */
export const LEAGUE_TOP4_COINS = 100;

export interface MatchReward {
  readonly total: Coins;
  /** Human-readable breakdown, shown on the post-match screen. */
  readonly lines: { label: string; amount: Coins }[];
}

/**
 * Coins for one finished match.
 *
 * `firstWinToday` is passed in rather than read here, because "what day is it"
 * is state and this file is pure — see the module header.
 */
export function matchReward(scored: number, conceded: number): MatchReward {
  const lines: { label: string; amount: Coins }[] = [];
  const won = scored > conceded;

  lines.push({ label: 'Match played', amount: PLAYED_COINS });
  if (won) lines.push({ label: 'Win', amount: WIN_COINS });

  const goals = Math.max(0, Math.floor(scored)) * GOAL_COINS;
  if (goals > 0) lines.push({ label: `Goals (${Math.floor(scored)})`, amount: goals });

  return { total: PLAYED_COINS + (won ? WIN_COINS : 0) + goals, lines };
}

/** Coins for reaching a knockout round (paid on ENTERING it, win or lose). */
export function knockoutReward(round: number, wonTournament: boolean): Coins {
  const base = WC_ROUND_COINS[round] ?? 0;
  return base + (wonTournament ? WC_TROPHY_COINS : 0);
}

/** Coins for a completed league season at `position` (1-based). */
export function seasonReward(position: number): MatchReward {
  const lines: { label: string; amount: Coins }[] = [
    { label: 'Season completed', amount: SEASON_COMPLETE_COINS },
  ];
  let total = SEASON_COMPLETE_COINS;
  if (position === 1) {
    lines.push({ label: 'League title', amount: LEAGUE_TITLE_COINS });
    total += LEAGUE_TITLE_COINS;
  } else if (position <= 4) {
    lines.push({ label: `Top four (${position})`, amount: LEAGUE_TOP4_COINS });
    total += LEAGUE_TOP4_COINS;
  }
  return { total, lines };
}

/** Local calendar day. Retained for any future daily reward. */
export function todayKey(now = new Date()): string {
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}
